import { ApplicationTypes, ApplicationState } from "./constants";
import { type Instruction, InstructionCategory } from './instruction_types';
import { type MachineLogicServices } from "@vse/MachineLogicServices";

export { type Instruction, InstructionCategory };
export * from './instruction_types';

export interface User {
  isAdmin: boolean;
}

export interface Notification {
  addListener: (listener: object) => void;
  removeListener: (listener: object) => void;
  notify: (listenerName: string, ...args: unknown[]) => void

  // Add anything you need that's missing
}

export interface ApplicationBase extends Notification{
  name: string;
  mIsMachineApp: boolean;
  getType: () => ApplicationTypes;
  getName: () => string;
  id: string;
  isRunning: () => boolean;
  isReadOnly: () => boolean;
  setName: (name: string) => void;
  getAsDownloadFormat: () => { applications: ApplicationBase[]}
  mState: ApplicationState;
  mLibrary: Library;
  stop: (onRequestSent?: (unknown) => void) => void;
  pause: (onRequestSent?: (unknown) => void) => void;
  resume: (onRequestSent?: (unknown) => void) => void;
  play: (runTimeConfigureation: unknown, onRequestSent?: (unknown) => void) => void;
  toJson: () => any;

  // Add anything you need that's missing
}

export interface Application extends ApplicationBase {
  robotParameters?: RobotParameters
  initialSequence: Sequence;
  currentSequence: Sequence;
  active: boolean;
  validationErrorCount: number;
  parameters: ApplicationParameters;
  selectSequenceByName: (name: string) => void;
  setName: (name: string) => void;
  getInitialSequence: () => Sequence;
  addSequence: (sequenceData: object) => Sequence;
  sequences: Sequence[];
  listSequences: () => string[];
  newSequence: () => Sequence;
  removeSequence: (sequenceName: string) => void;
  moveSequence: (fromIndex: number, toIndex: number) => void;
  getSequence: (sequenceName: string) => Sequence;
  getParametersObject: () => ApplicationParameters;
  invalidInstructionUuids: any;
  // Add anything you need that's missing
}

export interface ApplicationParameters extends ApplicationBase {
  listParametersNameOfType: (type: string[]) => any;
  parameters: any;
  json: any;
}
export interface InjectableMachineAppApplication extends ApplicationBase {
  mIsMachineApp: boolean;
}

interface PythonStorage {
  filename: string;
  code: string;
}

export interface PythonApplication extends ApplicationBase {
  getSingleSourceFile: () => PythonStorage;
  changeSourceFile: (filename: string, code: string | undefined) => void;
  mState: ApplicationState;
  dirty: boolean;
  // Add anything you need that's missing
}

type ResultCallback = (result: true | null) => void;

export interface ConfigManager extends Notification {
  inputs: Input[];
  dirty: boolean;
  numberOfAxes: () => number;
  numberOfRobots: () => number;
  listInputs: () => Input[];
  listOutputs: () => Output[];
  listRobots: () => any[];
  listAxes: () => Axis[];
  numberOfRealAxes: () => number;
  getInputByUuid: (uuid: string) => Input;
  getAxisByUuid: (uuid: string) => Axis;
  getOutputByUuid: (uuid: string) => Output;
  deploy: (callback: ResultCallback | void) => void;

  // Add anything you need that's missing
}

export interface Library extends Notification {
  validator: Validator
  configManager: ConfigManager
  newApplication: (applicationType?: ApplicationTypes) => ApplicationBase;
  removeApplication: (applicationId: string) => void;
  copyApplication: (application: ApplicationBase) => ApplicationBase;
  currentApplication: ApplicationBase;
  getMachineLogicAppList: () => Application[];
  getPythonAppList: () => PythonApplication[];
  getMachineAppApplications: () => InjectableMachineAppApplication[];
  getConfigManager: () => ConfigManager;
  getRobotAppsService: () => RobotAppsService;
  selectApplication: (app: ApplicationBase|null, forceRefresh?: boolean) => void;
  isVersionDownloadable: () => boolean;
  initialize: (onComplete: () => void, machineLogicServices?: MachineLogicServices) => void;

  /**
   * Ultimately the robot apps service should live outside the VSE.
   */
  robotAppsService?: unknown;

  /**
   * The m3DContext is a CAD implementation detail, it should not live in the VSE.
   * Its interface (IContext3D) is defined in the CAD as well, we can't import it from here.
   */
  m3DContext?: ThreeDContext;
  getCurrentApplication: () => ApplicationBase | null;
  getInstructionSet: () => any;
  getAllApplications: () => ApplicationBase[];

  // Add anything you need that's missing
}

export interface ThreeDContext {
  robotAppsService?: RobotAppsService;
}

// TODO: (AJ) This is defined in CAD, we should move it to vse.
export interface RobotAppsService {
  send: (message: string, data?: any) => void;
}

export interface MachinelogicLibrary extends Library {
  currentApplication: Application
}

export interface Validator {
  instructionsUsingSequence: (sequenceName: string, app: Application) => Instruction[];
}

export interface DynamicLoadedLibrary extends Notification {
  require: (onComplete: () => void) => void;
  mLibrary: Library;

  // Add anything you need that's missing
}

export interface Sequence extends Notification {
  mName: string;
  uuid: string;
  instructions: Instruction[];
  name: string;
  selectedInstruction: Instruction;
  robotParameters?: RobotParameters;
  isInitialSequence: () => boolean;
  getApp: () => Application;
  toJson: () => any;
  getName: () => string;
  getInstructionIndex: (instruction: Instruction) => number;
  newInstruction: (category: InstructionCategory, name: string, index?: number) => Instruction;
  getIsLocked: () => boolean;

  // Add anything you need that's missing
}

export interface Axis extends Notification {
   name: string;
   unit: string;
   type: string;
}

export interface Output extends Notification {
  name: string;
}

export interface Input extends Notification {
  uuid: string;
  name: string;
  deviceType: string;
}

interface RobotParameters {
  arrays: {
    id: string;
    patternConfig: { name: string; };
    foo: string;
  }[]
  clips: {
    id: string;
    keyframes: {
      waypoint?: unknown;
      waitForTime?: { time: number; };
      setPayload?: unknown;
      setSpeed?: unknown;
    }[];
  }[]
}

export type IoDeviceType = 'pushButtonModule' | 'powerSwitchModule' | 'digitalIOModule';
export type PneumaticDeviceType = "pneumatic" | "pneumatic_bag_gripper" | "ac_motor_with_vfd";

export enum Environment {
  CAD = 'cad',
  PENDANT = 'pendant'
}
